import React from 'react';
import { useNavigate } from 'react-router-dom';
import { useAudio } from './DassPage';
import { useAuth } from '../contexts/AuthContext';
import { API_CONFIG } from '../config/api';
import { saveOfflineAssessment } from '../utils/offlineAssessment';
import { toast } from 'react-toastify';

type DassSubscale = 'depression' | 'anxiety' | 'stress';

interface StarWellbeingData {
  scores: {
    categories: {
      inherent: {
        average: number;
      };
      coherent: {
        average: number;
      };
    };
  };
}

interface DassQuestion {
  id: string;
  text: string;
  subscale: DassSubscale;
}

interface AnswerOption {
  value: number;
  label: string;
  shortLabel: 'Never' | 'Sometimes' | 'Often' | 'Almost Always';
}

const STAR_COUNT = 4;
const TOTAL_LEVELS = 4;

const DASS_QUESTIONS: DassQuestion[] = [
  { id: '1', text: "I couldn't seem to experience any positive feeling at all.", subscale: 'depression' },
  { id: '2', text: "I found it difficult to work up the initiative to do things.", subscale: 'depression' },
  { id: '3', text: 'I felt that I had nothing to look forward to.', subscale: 'depression' },
  { id: '4', text: 'I felt down-hearted and blue.', subscale: 'depression' },
  { id: '5', text: 'I was unable to become enthusiastic about anything.', subscale: 'depression' },
  { id: '6', text: "I felt I wasn't worth much as a person.", subscale: 'depression' },
  { id: '7', text: 'I felt that life was meaningless.', subscale: 'depression' },
  { id: '8', text: 'I was aware of dryness of my mouth.', subscale: 'anxiety' },
  { id: '9', text: 'I experienced breathing difficulty (e.g. excessively rapid breathing, breathlessness in the absence of physical exertion).', subscale: 'anxiety' },
  { id: '10', text: 'I experienced trembling (e.g., in the hands).', subscale: 'anxiety' },
  { id: '11', text: 'I was worried about situations in which I might panic and make a fool of myself.', subscale: 'anxiety' },
  { id: '12', text: 'I felt I was close to panic.', subscale: 'anxiety' },
  { id: '13', text: 'I was aware of the action of my heart in the absence of physical exertion (e.g. sense of heart rate increase, heart missing a beat.)', subscale: 'anxiety' },
  { id: '14', text: 'I felt scared without any good reason.', subscale: 'anxiety' },
  { id: '15', text: 'I found it hard to wind down.', subscale: 'stress' },
  { id: '16', text: 'I tended to over-react to situations.', subscale: 'stress' },
  { id: '17', text: 'I felt that I was using a lot of nervous energy.', subscale: 'stress' },
  { id: '18', text: 'I found myself getting agitated.', subscale: 'stress' },
  { id: '19', text: 'I found it difficult to relax.', subscale: 'stress' },
  { id: '20', text: 'I was intolerant of anything that kept me from getting on with what I was doing.', subscale: 'stress' },
  { id: '21', text: 'I felt that I was rather touchy.', subscale: 'stress' }
];

// Create unique subscale identifiers to prevent duplicate key warnings
const getUniqueSubscale = (questionId: string, subscale: string) => `${subscale}-${questionId}`;

const ANSWER_OPTIONS: AnswerOption[] = [
  { value: 0, label: 'Did not apply to me at all', shortLabel: 'Never' },
  { value: 1, label: 'Applied to me to some degree, or some of the time', shortLabel: 'Sometimes' },
  { value: 2, label: 'Applied to me to a considerable degree, or a good part of the time', shortLabel: 'Often' },
  { value: 3, label: 'Applied to me very much, or most of the time', shortLabel: 'Almost Always' }
];

const getDassLevel = (questionId: string) => {
  const numberId = parseInt(questionId, 10);
  if (numberId <= 5) return 1;
  if (numberId <= 10) return 2;
  if (numberId <= 15) return 3;
  return 4;
};


const StarIcon: React.FC<{ filled?: boolean }> = ({ filled }) => (
  <svg
    className={`mental-health-questionnaire__star${filled ? ' mental-health-questionnaire__star--filled' : ''}`}
    viewBox="0 0 24 24"
    aria-hidden="true"
  >
    <path d="M12 17.27L18.18 21l-1.64-7.03L22 9.24l-7.19-.61L12 2 9.19 8.63 2 9.24l5.46 4.73L5.82 21z" />
  </svg>
);

const CircularProgress: React.FC<{ percentage: number; color: string; label: string }> = ({ percentage, color, label }) => {
  const radius = 40;
  const circumference = 2 * Math.PI * radius;
  const strokeDasharray = circumference;
  const strokeDashoffset = circumference - (percentage / 100) * circumference;

  // Ensure percentage is a valid number
  const safePercentage = isNaN(percentage) ? 0 : Math.max(0, Math.min(100, percentage));

  return (
    <div className="mental-health-questionnaire__circular-progress">
      <div className="mental-health-questionnaire__circular-chart">
        <svg width="100" height="100" viewBox="0 0 100 100">
          <circle
            cx="50"
            cy="50"
            r={radius}
            fill="none"
            stroke="#e5e7eb"
            strokeWidth="8"
          />
          <circle
            cx="50"
            cy="50"
            r={radius}
            fill="none"
            stroke={color || '#059669'}
            strokeWidth="8"
            strokeDasharray={strokeDasharray}
            strokeDashoffset={circumference - (safePercentage / 100) * circumference}
            strokeLinecap="round"
            transform="rotate(-90 50 50)"
            className="mental-health-questionnaire__circular-fill"
          />
        </svg>
        <div className="mental-health-questionnaire__circular-center">
          <div className="mental-health-questionnaire__circular-percentage">{safePercentage}%</div>
        </div>
      </div>
      <div className="mental-health-questionnaire__circular-label">{label || 'Unknown'}</div>
    </div>
  );
};


// Add global error handler to catch className.split errors
if (typeof window !== 'undefined') {
  const originalError = console.error;
  console.error = (...args) => {
    // Filter out the specific className.split error that might be from browser extensions
    // But allow React duplicate key warnings to be logged
    const errorMessage = args.join(' ');
    if (errorMessage.includes('className.split is not a function') ||
        errorMessage.includes('el.className.split is not a function')) {
      // Suppress this specific error as it's likely from browser extensions
      return;
    }
    originalError.apply(console, args);
  };
}

const MentalHealthQuestionnairePage: React.FC = () => {
  const navigate = useNavigate();
  const { token, isAuthenticated, user } = useAuth();

  const [currentIndex, setCurrentIndex] = React.useState<number>(0);
  const [answers, setAnswers] = React.useState<Record<string, number>>({});
  const [isComplete, setIsComplete] = React.useState<boolean>(false);
  const [showLevelModal, setShowLevelModal] = React.useState<boolean>(false);
  const [completedLevel, setCompletedLevel] = React.useState<number | null>(null);
  const [isInitialRender, setIsInitialRender] = React.useState<boolean>(true);

  // Audio functionality
  const { isPlaying, volume, isMuted, togglePlayPause, toggleMute, setVolume } = useAudio();

  // Helper to read Star of Well-Being data from localStorage
  const getStarWellbeingData = React.useCallback((): StarWellbeingData | null => {
    try {
      const STAR_CACHE_KEY = user?.id ? `lifepsensor_star_wellbeing_cache_${user.id}` : 'lifepsensor_star_wellbeing_cache';
      const cachedData = localStorage.getItem(STAR_CACHE_KEY);

      if (!cachedData) {
        return null;
      }

      const parsedData = JSON.parse(cachedData) as StarWellbeingData;

      // Validate that the required structure exists
      if (parsedData?.scores?.categories?.inherent?.average === undefined ||
          parsedData?.scores?.categories?.coherent?.average === undefined) {

        return null;
      }

      return parsedData;
    } catch (error) {

      return null;
    }
  }, [user?.id]);

  // Compute inherent and coherent percentages from Star of Well-Being data
  const starWellbeingPercentages = React.useMemo(() => {
    const starData = getStarWellbeingData();

    if (!starData) {
      return { inherent: 0, coherent: 0 };
    }

    const inherentAvg = starData.scores.categories.inherent.average;
    const coherentAvg = starData.scores.categories.coherent.average;

    // Convert from 0-4 scale to 0-100% and add 2%
    const inherentPercentage = Math.round((inherentAvg / 4) * 100) + 2;
    const coherentPercentage = Math.round((coherentAvg / 4) * 100) + 2;

    // Clamp between 0-100
    return {
      inherent: Math.max(0, Math.min(100, inherentPercentage)),
      coherent: Math.max(0, Math.min(100, coherentPercentage))
    };
  }, [getStarWellbeingData]);

  // Helper function to determine progress bar color based on percentage
  const getStarProgressColor = (percentage: number) => {
    if (percentage >= 80) return '#22c55e'; // Green for 80%+ (high scores, good)
    if (percentage >= 40) return '#ea580c'; // Orange for 40-79%
    return '#dc2626'; // Red for 0-39% (low scores, bad)
  };

  // Error boundary for this component
  React.useEffect(() => {
    const handleError = (event: ErrorEvent) => {
      if (event.error && event.error.message &&
          (event.error.message.includes('className.split is not a function') ||
           event.error.message.includes('el.className.split is not a function'))) {
        event.preventDefault();
        console.warn('Suppressed className.split error (likely from browser extension)');
      }
    };

    window.addEventListener('error', handleError);
    return () => window.removeEventListener('error', handleError);
  }, []);

  const handleVolumeChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setVolume(Number(e.target.value));
  };

  const totalQuestions = DASS_QUESTIONS.length;
  const currentQuestion = DASS_QUESTIONS[currentIndex];
  const currentLevel = currentQuestion ? getDassLevel(currentQuestion.id) : TOTAL_LEVELS;
  const progressPercentage = isComplete ? 100 : ((currentIndex + 1) / totalQuestions) * 100;
  const selectedValue = currentQuestion ? answers[currentQuestion.id] : undefined;

  const handleSelectOption = (value: number) => {
    if (!currentQuestion) return;
    setAnswers(prev => ({
      ...prev,
      [currentQuestion.id]: value
    }));
  };

  const handleSubmit = async () => {
    // Validate that all questions are answered
    const answeredQuestions = Object.keys(answers).length;
    if (answeredQuestions < totalQuestions) {
      alert('Please answer all questions before submitting.');
      return;
    }

    try {
      // Format responses for API
      const formattedResponses = Object.entries(answers).map(([questionId, answer]) => {
        const question = DASS_QUESTIONS.find(q => q.id === questionId);
        return {
          questionId,
          question: question?.text || 'Question text not found',
          answer,
          subscale: question ? getUniqueSubscale(question.id, question.subscale) : 'unknown'
        };
      });

      // Calculate subscale scores
      const depressionRaw = DASS_QUESTIONS.filter(q => q.subscale === 'depression')
        .reduce((sum, question) => sum + (answers[question.id] ?? 0), 0);
      const anxietyRaw = DASS_QUESTIONS.filter(q => q.subscale === 'anxiety')
        .reduce((sum, question) => sum + (answers[question.id] ?? 0), 0);
      const stressRaw = DASS_QUESTIONS.filter(q => q.subscale === 'stress')
        .reduce((sum, question) => sum + (answers[question.id] ?? 0), 0);

      // Calculate final scores (raw score × 2)
      const depressionScore = depressionRaw * 2;
      const anxietyScore = anxietyRaw * 2;
      const stressScore = stressRaw * 2;

      // Overall score is average of the three subscales
      const overallScore = Math.round((depressionScore + anxietyScore + stressScore) / 3);

      if (!isAuthenticated) {
        // Public version - save locally for later sync

        const offlineData = {
          assessmentData: formattedResponses,
          responses: formattedResponses,
          duration: 0,
          savedAt: new Date().toISOString(),
          type: 'dass1'
        };

        saveOfflineAssessment(offlineData, 'dass1');

        // Show user feedback
        toast.info('Assessment saved locally! Your results will be synced to your account when you log in.', {
          position: "top-center",
          autoClose: 5000,
          hideProgressBar: false,
          closeOnClick: true,
          pauseOnHover: true,
          draggable: true,
        });

        // Show completion on same page
        setIsComplete(true);
      } else if (isAuthenticated && token) {
        // Authenticated version - save to database

        const response = await fetch(API_CONFIG.ENDPOINTS.MENTAL_HEALTH, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`,
          },
          credentials: 'include',
          body: JSON.stringify({ responses: formattedResponses, duration: 0 })
        });

        if (response.ok) {
          const result = await response.json();

          // Show completion on same page
          setIsComplete(true);
        } else {
          const errorData = await response.json();
          throw new Error(`Failed to save assessment: ${errorData.message}`);
        }
      } else {
        // Fallback error for unexpected state
        toast.error('Unable to save assessment. Please try again.');
      }
    } catch (error) {
      toast.error('Error submitting assessment. Please try again.');
    }
  };

  const handleNext = () => {
    if (currentIndex === totalQuestions - 1) {
      // Assessment completed, submit the results
      handleSubmit();
      return;
    }

    const prevLevel = currentLevel;
    const nextIndex = Math.min(currentIndex + 1, totalQuestions - 1);
    setCurrentIndex(nextIndex);

    // Check if we've completed a level
    const nextQuestion = DASS_QUESTIONS[nextIndex];
    const nextLevel = nextQuestion ? getDassLevel(nextQuestion.id) : TOTAL_LEVELS;

    if (nextLevel > prevLevel) {
      setCompletedLevel(prevLevel);
      setShowLevelModal(true);
      setTimeout(() => {
        setShowLevelModal(false);
        setCompletedLevel(null);
      }, 3000);
    }
  };

  const handleBack = () => {
    if (isComplete) {
      setIsComplete(false);
      return;
    }
    setCurrentIndex(prev => Math.max(prev - 1, 0));
  };

  const handleRestart = () => {
    setAnswers({});
    setCurrentIndex(0);
    setIsComplete(false);
  };

  const handleContinue = () => {
    navigate('/login');
  };

  const isLastQuestion = currentIndex === totalQuestions - 1;

  // Remove initial animation after first render
  React.useEffect(() => {
    const timer = setTimeout(() => {
      setIsInitialRender(false);
    }, 600); // Match animation duration

    return () => clearTimeout(timer);
  }, []);

  // Helper functions for circular progress
  const getScorePercentage = (score: number, maxScore: number = 42) => {
    return Math.round((score / maxScore) * 100);
  };

  const getScoreColor = (score: number, type: 'depression' | 'anxiety' | 'stress') => {
    let maxNormal: number;
    let maxModerate: number;

    switch (type) {
      case 'depression':
        maxNormal = 18; // Normal: 0-18
        maxModerate = 33; // Moderate: 26-33
        break;
      case 'anxiety':
        maxNormal = 14; // Normal: 0-14
        maxModerate = 28; // Moderate: 19-28
        break;
      case 'stress':
        maxNormal = 28; // Normal: 0-28
        maxModerate = 42; // Moderate: 37-42
        break;
    }

    if (score <= maxNormal) return '#059669'; // Green for low/normal
    if (score <= maxModerate) return '#ea580c'; // Orange for moderate
    return '#dc2626'; // Red for high/severe
  };

  const completionSummary = React.useMemo(() => {
    if (!isComplete) return null;

    try {
      const depressionTotal = DASS_QUESTIONS.filter(q => q.subscale === 'depression')
        .reduce((sum, question) => sum + (answers[question.id] ?? 0), 0);
      const anxietyTotal = DASS_QUESTIONS.filter(q => q.subscale === 'anxiety')
        .reduce((sum, question) => sum + (answers[question.id] ?? 0), 0);
      const stressTotal = DASS_QUESTIONS.filter(q => q.subscale === 'stress')
        .reduce((sum, question) => sum + (answers[question.id] ?? 0), 0);

      return {
        depression: Math.max(0, depressionTotal * 2),
        anxiety: Math.max(0, anxietyTotal * 2),
        stress: Math.max(0, stressTotal * 2)
      };
    } catch (error) {

      return null;
    }
  }, [isComplete, answers]);

  return (
    <div className="mental-health-questionnaire">
      {/* Audio controls */}
      <div className="mental-health-questionnaire__audio-controls">
        <button className="mental-health-questionnaire__audio-btn" onClick={togglePlayPause}>
          {isPlaying ? '⏸' : '▶'}
        </button>
        <button className="mental-health-questionnaire__audio-btn" onClick={toggleMute}>
          {isMuted ? '🔇' : '🔊'}
        </button>
        <input
          type="range"
          min="0"
          max="100"
          value={volume}
          onChange={handleVolumeChange}
          className="mental-health-questionnaire__volume-slider"
        />
      </div>

      <div className="mental-health-questionnaire__container">
        <div className="mental-health-questionnaire__progress">
          <div className="mental-health-questionnaire__level-badge">
            <span className="mental-health-questionnaire__level-number">
              {isComplete ? TOTAL_LEVELS : currentLevel}
            </span>
            <span className="mental-health-questionnaire__level-label">Level</span>
          </div>
          <div className="mental-health-questionnaire__progress-track" aria-hidden="true">
            <div
              className="mental-health-questionnaire__progress-fill"
              style={{ width: `${progressPercentage}%` }}
            />
          </div>
        </div>

        <section
          className={`mental-health-questionnaire__card${isInitialRender ? ' mental-health-questionnaire__card--initial' : ''}`}
          aria-labelledby="question-heading"
        >
          {isComplete ? (
            <div className="mental-health-questionnaire__completion">
              <h1 id="question-heading" className="mental-health-questionnaire__completion-title">
                Assessment Complete!
              </h1>
              <p className="mental-health-questionnaire__completion-subtitle">
                Here are your DASS-21 results showing your current levels of depression, anxiety, and stress.
              </p>

              {completionSummary && (
                <div className="mental-health-questionnaire__completion-grid">
                  <CircularProgress
                    key="depression-progress"
                    percentage={getScorePercentage(completionSummary.depression)}
                    color={getScoreColor(completionSummary.depression, 'depression')}
                    label="Depression"
                  />
                  <CircularProgress
                    key="anxiety-progress"
                    percentage={getScorePercentage(completionSummary.anxiety)}
                    color={getScoreColor(completionSummary.anxiety, 'anxiety')}
                    label="Anxiety"
                  />
                  <CircularProgress
                    key="stress-progress"
                    percentage={getScorePercentage(completionSummary.stress)}
                    color={getScoreColor(completionSummary.stress, 'stress')}
                    label="Stress"
                  />
                </div>
              )}

              {/* Star of Well-Being Coherent and Inherent Categories */}
              {(starWellbeingPercentages.inherent > 0 || starWellbeingPercentages.coherent > 0) && (
                <div className="mental-health-questionnaire__star-wellbeing-section">
                  <div className="mental-health-questionnaire__completion-grid">
                    <CircularProgress
                      key="inherent-progress"
                      percentage={starWellbeingPercentages.inherent}
                      color={getStarProgressColor(starWellbeingPercentages.inherent)}
                      label="Inherent"
                    />
                    <CircularProgress
                      key="coherent-progress"
                      percentage={starWellbeingPercentages.coherent}
                      color={getStarProgressColor(starWellbeingPercentages.coherent)}
                      label="Coherent"
                    />
                  </div>
                </div>
              )}

              <div className="mental-health-questionnaire__actions mental-health-questionnaire__actions--center">
                <button
                  type="button"
                  className="mental-health-questionnaire__action-btn mental-health-questionnaire__action-btn--secondary"
                  onClick={handleContinue}
                >
                  Continue
                </button>
                <button
                  type="button"
                  className="mental-health-questionnaire__action-btn mental-health-questionnaire__action-btn--primary"
                  onClick={handleRestart}
                >
                  Start Again
                </button>
              </div>
            </div>
          ) : currentQuestion ? (
            <>
              <div className="mental-health-questionnaire__card-header">
                <span className="mental-health-questionnaire__card-pill">
                  Level {currentLevel} · {TOTAL_LEVELS} Levels
                </span>
                <h1 id="question-heading" className="mental-health-questionnaire__question">
                  {currentQuestion.text}
                </h1>
              </div>

              <div className="mental-health-questionnaire__options" role="list">
                {ANSWER_OPTIONS.map((option) => {
                  const isSelected = selectedValue === option.value;

                  return (
                    <button
                      key={option.value}
                      type="button"
                      className={`mental-health-questionnaire__option-card${isSelected ? ' mental-health-questionnaire__option-card--selected' : ''}`}
                      onClick={() => handleSelectOption(option.value)}
                      aria-pressed={isSelected}
                    >
                      <div className="mental-health-questionnaire__stars" aria-hidden="true">
                        {Array.from({ length: STAR_COUNT }).map((_, index) => (
                          <StarIcon key={`star-${option.value}-${index}`} filled={index < option.value + 1} />
                        ))}
                      </div>
                      <div className="mental-health-questionnaire__option-text">
                        <p className="mental-health-questionnaire__option-label">{option.shortLabel}</p>
                        <span className="mental-health-questionnaire__option-helper">{option.label}</span>
                      </div>
                    </button>
                  );
                })}
              </div>

              <div className="mental-health-questionnaire__actions">
                <button
                  type="button"
                  className="mental-health-questionnaire__action-btn mental-health-questionnaire__action-btn--secondary"
                  onClick={handleBack}
                  disabled={currentIndex === 0}
                >
                  Back
                </button>
                <button
                  type="button"
                  className="mental-health-questionnaire__action-btn mental-health-questionnaire__action-btn--primary"
                  onClick={handleNext}
                  disabled={selectedValue === undefined}
                >
                  {isLastQuestion ? 'Finish Assessment' : 'Next'}
                </button>
              </div>
            </>
          ) : null}
        </section>
      </div>

      {showLevelModal && completedLevel && (
        <div
          className="mental-health-questionnaire__level-modal"
          role="status"
          aria-live="polite"
        >
          🎉 Level {completedLevel} Complete! 🎉
        </div>
      )}

    </div>
  );
};

export default MentalHealthQuestionnairePage;


